import { InputSummary, InputSummaryItem } from '../../input-summary';
import InputSummaryUtils from '../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../data-structures/unit-value.model';
import { FinancialFeeType } from '../financial/financial-fee-type.enum';

/**
 * An interface used to define the class structure for the feedstock financial information
 */
export interface MaterialFinancialInterface {
  feeType: string;
  price: UnitValue;
  amount: UnitValue;
  useTransport: boolean;
  transportPrice: UnitValue;
  transportAmount: UnitValue;
  feedstockCpi: UnitValue;
  transportCpi: UnitValue;
}

/**
 * constructs the FeedstockFinancial class.
 * @param param is used to construct the class. If null or not provided, the class will be set to default values
 */
export class FeedstockFinancial {
  feeType: string;
  public price: UnitValue;
  public amount: UnitValue;
  public useTransport: boolean;
  public transportPrice: UnitValue;
  public transportAmount: UnitValue;
  public feedstockCpi: UnitValue;
  public transportCpi: UnitValue;

  /**
   * constructs the FeedstockFinancial class.
   * @param param is used to construct the class. If null or not provided, the class will be set to default values
   */
  constructor(param: MaterialFinancialInterface = {
    feeType: null,
    price: new UnitValue(),
    amount: new UnitValue(),
    useTransport: false,
    transportPrice: new UnitValue(),
    transportAmount: new UnitValue(),
    feedstockCpi: new UnitValue(),
    transportCpi: new UnitValue()
  }) {
    this.feeType = param.feeType;
    this.price = new UnitValue(param.price);
    this.amount = new UnitValue(param.amount);
    this.feedstockCpi = new UnitValue(param.feedstockCpi);
    this.useTransport = param.useTransport;
    this.transportPrice = new UnitValue(param.transportPrice);
    this.transportAmount = new UnitValue(param.transportAmount);
    this.transportCpi = new UnitValue(param.transportCpi);
  }

  /**
   * returns a separate instance of the provided FeedstockFinancial obj
   * @param feedstockFinancial will be returned as a separate instance
   */
  static deepCopy(feedstockFinancial: FeedstockFinancial) {
    let copy;
    if (feedstockFinancial) {
      copy = new FeedstockFinancial(feedstockFinancial);
      return copy;
    }
    throw new Error('feedstock financial copy failed');
  }

  /**
   * returns an InputSummary obj that contains all data available for this instance
   */
  getInputSummary(path: string[]) {
    const inputSummary = new InputSummary();
    inputSummary.inputs = this.getSummaryInputs(path);
    return inputSummary;
  }

  /**
   * Returns an array of InputSummaryItem containing inputs for each member of the FeedstockFinancial class
   */
  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    let feetype = '';
    switch (this.feeType) {
      case 'cost':
        feetype = 'INPUT_SUMMARY.FINANCIAL.FEE_TYPE.COST';
        break;
      case 'income':
        feetype = 'INPUT_SUMMARY.FINANCIAL.FEE_TYPE.INCOME';
        break;
      case 'free':
        feetype = 'INPUT_SUMMARY.FINANCIAL.FEE_TYPE.FREE';
        break;
      case 'tipping fee':
        feetype = 'INPUT_SUMMARY.FINANCIAL.FEE_TYPE.TIPPING_FEE';
        break;
    }
    inputs = InputSummaryUtils.performPush(inputs, FeedstockFinancialPropName.feeType, feetype, [...path, 'feeType']);
    if (this.feeType !== FinancialFeeType.FREE) {
      inputs = InputSummaryUtils.performPush(inputs, FeedstockFinancialPropName.price, this.price, [...path, 'price']);
      inputs = InputSummaryUtils.performPush(inputs, FeedstockFinancialPropName.amount, this.amount, [...path, 'amount']);
      inputs = InputSummaryUtils.performPush(inputs, FeedstockFinancialPropName.feedstockCpi, this.feedstockCpi, [...path, 'feedstockCpi']);
    }
    inputs = InputSummaryUtils.performPush(inputs, FeedstockFinancialPropName.useTransport, this.useTransport, [...path, 'useTransport']);
    if (this.useTransport) {
      inputs = InputSummaryUtils.performPush(inputs, FeedstockFinancialPropName.transportPrice, this.transportPrice, [...path, 'transportPrice']);
      inputs = InputSummaryUtils.performPush(inputs, FeedstockFinancialPropName.transportAmount, this.transportAmount, [...path, 'transportAmount']);
      inputs = InputSummaryUtils.performPush(inputs, FeedstockFinancialPropName.transportCpi, this.transportCpi, [...path, 'transportCpi']);
    }
    return inputs;
  }
}

export enum FeedstockFinancialPropName {
  feeType = 'INPUT_SUMMARY.FEEDSTOCK.FINANCIAL.FEE_TYPE',
  useTransport = 'INPUT_SUMMARY.FEEDSTOCK.FINANCIAL.USE_TRANSPORT',
  price = 'INPUT_SUMMARY.FEEDSTOCK.FINANCIAL.PRICE',
  amount = 'INPUT_SUMMARY.FEEDSTOCK.FINANCIAL.AMOUNT',
  transportPrice = 'INPUT_SUMMARY.FEEDSTOCK.FINANCIAL.TRANSPORT_PRICE',
  transportAmount = 'INPUT_SUMMARY.FEEDSTOCK.FINANCIAL.TRANSPORT_AMOUNT',
  feedstockCpi = 'INPUT_SUMMARY.FEEDSTOCK.FINANCIAL.FEEDSTOCK_CPI',
  transportCpi = 'INPUT_SUMMARY.FEEDSTOCK.FINANCIAL.TRANSPORT_CPI',
  id_feeType = 'fee type',
  id_useTransport = 'use transportation cost',
  id_price = 'price',
  id_amount = 'amount',
  id_transportPrice = 'transport price',
  id_transportAmount = 'transport amount',
  id_feedstockCpi = 'feedstock CPI',
  id_transportCpi = 'transport CPI'
}
